import React, { useEffect, useRef, useState } from 'react';
import { Button, Col, Row } from 'reactstrap';
import { toast } from 'react-toastify';
import { useHistory, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import PlusIcon from 'mdi-react/PlusIcon';
import Api from '../../../util/api';
import Loading from '../../../shared/components/custom/Loading';
import { partial } from '../../../util/translation/translation';
import useConfig from '../../../util/useConfig';
import ConnectReceiverForm from './ConnectReceiverForm';
import ConnectReceiverCard from './ConnectReceiverCard';
import ConnectReport from './ConnectReport';
import { countryFromCurrency, receiverStatus } from '../connect';

const p = partial('connect');

// The club's payouts through Stripe Connect: the club's own Stripe accounts
// (receivers) its payments go to. Stripe sends the admin back here after
// onboarding (?connect=return) or when the onboarding link has expired
// (?connect=refresh: a new link, straight back to Stripe).
const ConnectPayouts = () => {
  const config = useConfig();
  const history = useHistory();
  const location = useLocation();
  const isSuperAdmin = useSelector((state) =>
    state.admin.me?.permissions?.includes('super-admin')
  );
  const [receivers, setReceivers] = useState(null);
  const [loading, setLoading] = useState(true);
  // Super-users may add more receivers (e.g. for a combined club's plan)
  const [adding, setAdding] = useState(false);
  const handledReturn = useRef(false);

  const errorMessage = (err, fallback) =>
    typeof err === 'string' && err ? err : fallback;

  const fetchReceivers = async () => {
    try {
      setReceivers(await Api.connect.getReceivers());
    } catch (err) {
      toast.error(errorMessage(err, p('receiversRetrievingERROR')));
    }
  };

  const replaceReceiver = (receiver) =>
    setReceivers((current) =>
      (current || []).map((item) => (item.id === receiver.id ? receiver : item))
    );

  // Stripe sends the admin back to this page, with what happened in the query
  const onboardingLinks = (receiverId) => {
    const base = `${window.location.origin}${window.location.pathname}`;
    return {
      returnUrl: `${base}?connect=return&receiver=${receiverId}`,
      refreshUrl: `${base}?connect=refresh&receiver=${receiverId}`,
    };
  };

  const startOnboarding = async (receiverId) => {
    setLoading(true);
    try {
      const { url } = await Api.connect.getOnboardingLink(
        receiverId,
        onboardingLinks(receiverId)
      );
      // Stays loading while the browser leaves for Stripe
      window.location.assign(url);
      return true;
    } catch (err) {
      setLoading(false);
      toast.error(errorMessage(err, p('onboardingLinkERROR')));
      return false;
    }
  };

  const refresh = async (receiverId, { quiet = false } = {}) => {
    setLoading(true);
    try {
      const receiver = await Api.connect.refreshReceiver(receiverId);
      replaceReceiver(receiver);
      if (!quiet) {
        toast.success(p('statusRefreshed'));
      }
      return receiver;
    } catch (err) {
      toast.error(errorMessage(err, p('refreshERROR')));
      return null;
    } finally {
      setLoading(false);
    }
  };

  const openDashboard = async (receiverId) => {
    // Opened now, while it still counts as the click (popup blockers)
    const tab = window.open('', '_blank');
    try {
      const { url } = await Api.connect.getDashboardLink(receiverId);
      if (tab) {
        tab.opener = null;
        tab.location.href = url;
      } else {
        window.location.assign(url);
      }
    } catch (err) {
      tab?.close();
      toast.error(errorMessage(err, p('dashboardLinkERROR')));
    }
  };

  const onCreate = async (values) => {
    setLoading(true);
    let receiver;
    try {
      receiver = await Api.connect.createReceiver({
        name: values.name.trim(),
        country: values.country,
      });
    } catch (err) {
      setLoading(false);
      toast.error(errorMessage(err, p('createERROR')));
      return;
    }
    setReceivers((current) => [...(current || []), receiver]);
    setAdding(false);
    await startOnboarding(receiver.id);
  };

  useEffect(() => {
    const init = async () => {
      const params = new URLSearchParams(location.search);
      const action = params.get('connect');
      const receiverId = params.get('receiver');
      if (action === 'refresh' && receiverId && !handledReturn.current) {
        handledReturn.current = true;
        // The link expired: a new one, straight back to Stripe
        if (await startOnboarding(receiverId)) {
          return;
        }
        // No new link: show the receivers (the button to try again)
        history.replace(location.pathname);
      }
      await fetchReceivers();
      if (action === 'return' && receiverId && !handledReturn.current) {
        handledReturn.current = true;
        history.replace(location.pathname);
        // Back from onboarding: read the status from Stripe now
        const receiver = await refresh(receiverId, { quiet: true });
        if (receiver) {
          const status = receiverStatus(receiver);
          if (status === 'ready') {
            toast.success(p('backReady'));
          } else if (status === 'onboarding' || status === 'needsDetails') {
            toast.info(p('backNotFinished'));
          } else {
            toast.success(p('backSubmitted'));
          }
        }
      }
      setLoading(false);
    };
    init();
    // Runs once: the handlers are redefined every render
    // eslint-disable-next-line
  }, []);

  const hasReceivers = receivers?.length > 0;
  const showForm = receivers !== null && (!hasReceivers || adding);

  return (
    <>
      <Loading loading={loading} />
      <Row>
        <Col md={12} className="d-flex flex-wrap align-items-start">
          <p className="mb-3">{p('payoutsIntro')}</p>
          {hasReceivers && isSuperAdmin && !adding && (
            <Button
              type="button"
              color="primary"
              size="sm"
              className="ml-auto"
              onClick={() => setAdding(true)}
            >
              <PlusIcon size={16} /> {p('addReceiver')}
            </Button>
          )}
        </Col>
      </Row>
      <Row>
        <Col lg={8} className="settings-form">
          {showForm && (
            <ConnectReceiverForm
              onSubmit={onCreate}
              isFirst={!hasReceivers}
              defaultName={config?.club_name || ''}
              defaultCountry={countryFromCurrency(config?.currencyCaps)}
              onCancel={hasReceivers ? () => setAdding(false) : undefined}
            />
          )}
          {(receivers || []).map((receiver) => (
            <ConnectReceiverCard
              key={receiver.id}
              receiver={receiver}
              onContinueOnboarding={() => startOnboarding(receiver.id)}
              onOpenDashboard={() => openDashboard(receiver.id)}
              onRefresh={() => refresh(receiver.id)}
            />
          ))}
          {receivers === null && !loading && (
            <Button type="button" outline size="sm" onClick={fetchReceivers}>
              {p('tryAgain')}
            </Button>
          )}
        </Col>
      </Row>
      {hasReceivers && (
        <Row>
          <Col lg={8}>
            <h4 className="bold mt-3 mb-2">{p('report')}</h4>
            <ConnectReport />
          </Col>
        </Row>
      )}
    </>
  );
};

export default ConnectPayouts;
